import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Image from "next/image";
import React, {
	Dispatch,
	Fragment,
	SetStateAction,
	useMemo,
	useState,
} from "react";

import { GetResourceResponseDto } from "@onair/repositories";
import { getResourceFileIcon } from "@onair/utils/icon";
import UpsetModalResource from "./update-resource";
import { Button } from "@mui/material";

export interface ResourceItemProps {
	resource: GetResourceResponseDto & { isNew?: boolean };
	selectResourceId: string | null;
	currentResourceId: string | null;
	setCurrentResourceId: Dispatch<SetStateAction<string | null>>;
	handleSelectResourceId: (id: string) => void;
	isMultiple: boolean;
	multipleResources: string[];
	setMultipleResources: Dispatch<SetStateAction<string[]>>;
	onDelete: (id: string) => void;
	refetch: () => void;
	onSelectResource?: (resource: GetResourceResponseDto) => void;
}

const MAX_NAME_FILE_LENGTH = 17;

const ResourceItem = (props: ResourceItemProps) => {
	const {
		resource,
		selectResourceId,
		currentResourceId,
		setCurrentResourceId,
		isMultiple,
		multipleResources,
		setMultipleResources,
		handleSelectResourceId,
		onDelete,
		refetch,
		onSelectResource,
	} = props;
	const {
		id,
		thumbnailUrl,
		linkUrl,
		fileName,
		fileType,
		capacity,
		isNew = false,
		name,
	} = resource;

	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);
	const resourceDisabled = !linkUrl;

	const isSelected = useMemo(
		() =>
			isMultiple
				? multipleResources.includes(id)
				: selectResourceId === id || currentResourceId === id,
		[isMultiple, multipleResources, selectResourceId, currentResourceId, id],
	);

	const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
		setCurrentResourceId(id);
	};

	const handleCloseMenu = () => {
		setAnchorEl(null);
		setCurrentResourceId(null);
	};

	const handleDeleteResource = () => {
		onDelete(id);
	};

	const onHandleSelectResource = (resourceId: string) => {
		if (isMultiple) {
			const isResourceSelected = multipleResources.findIndex(
				(currentResourceId) => currentResourceId === resourceId,
			);
			const updatedResources =
				isResourceSelected !== -1
					? multipleResources.filter(
							(currentResourceId) => currentResourceId !== resourceId,
						)
					: [...multipleResources, resourceId];

			setMultipleResources(updatedResources);
		} else {
			if (selectResourceId === resourceId) {
				handleSelectResourceId(null);
			} else {
				handleSelectResourceId(resourceId);
			}
		}
	};

	return (
		<Grid item xs={24} sm={6} key={id}>
			<Card
				variant="outlined"
				className="p-2 rounded-md"
				sx={{
					backgroundColor: isNew ? "rgba(0, 80, 255, 0.08)" : "grey.200",
					borderColor: isSelected ? "primary.main" : "grey.200",
				}}
			>
				<Stack
					direction="row"
					spacing={1.5}
					justifyContent="space-between"
					alignItems="center"
					className={resourceDisabled ? "opacity-50 cursor-not-allowed" : ""}
					onClick={() => {
						if (!resourceDisabled) {
							onHandleSelectResource(id);
							onSelectResource?.(resource);
						}
					}}
				>
					<Stack direction="row" spacing={2}>
						<Box className="flex overflow-hidden rounded-md w-12 h-12 items-center justify-center">
							<Image
								loading="lazy"
								src={getResourceFileIcon(fileType, thumbnailUrl)} // Fallback icon
								alt={`thumbnail ${fileName + id}`}
								width={48}
								height={48}
								className="object-cover h-full"
							/>
						</Box>

						<Stack direction="column">
							<Tooltip title={fileName}>
								<Fragment>
									<Typography
										variant="body2"
										className="text-sm font-normal overflow-hidden text-ellipsis max-w-[120px] web break-words line-clamp-1"
									>
										{fileName.length > MAX_NAME_FILE_LENGTH
											? `${fileName.slice(0, MAX_NAME_FILE_LENGTH).trim()}...`
											: fileName}
									</Typography>

									<Typography variant="caption" className="font-light">
										{capacity}
									</Typography>
								</Fragment>
							</Tooltip>
						</Stack>
					</Stack>

					<IconButton
						id={`resource-menu-button-${id}`}
						aria-label="options"
						onClick={handleClick}
						aria-haspopup="true"
						aria-controls={anchorEl ? "resource-menu" : undefined}
					>
						<MoreVertIcon />
					</IconButton>

					<Menu
						id="resource-menu"
						anchorEl={anchorEl}
						open={open}
						onClose={handleCloseMenu}
						MenuListProps={{
							"aria-labelledby": `resource-menu-button-${id}`,
						}}
						anchorOrigin={{
							vertical: "bottom",
							horizontal: "center",
						}}
						transformOrigin={{
							vertical: "top",
							horizontal: "left",
						}}
					>
						<UpsetModalResource
							id={id}
							fileName={fileName}
							fileType={fileType!}
							refetch={refetch}
						>
							<MenuItem>
								<ListItemIcon>
									<EditIcon />
								</ListItemIcon>
								<ListItemText>Đổi tên</ListItemText>
							</MenuItem>
						</UpsetModalResource>
						<MenuItem onClick={handleDeleteResource}>
							<ListItemIcon>
								<DeleteIcon />
							</ListItemIcon>
							<ListItemText>Xoá</ListItemText>
						</MenuItem>
					</Menu>
				</Stack>
			</Card>
		</Grid>
	);
};

export default ResourceItem;
